import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import translations from 'src/internationalisation/translation-map.json';
import { useParams } from 'react-router';

import {
  Table,
  Button,
  Field,
  Loader,
  Spinner,
  Select,
  Flex,
  Heading,
  Spacer,
} from '@oliasoft-open-source/react-ui-library';
import { getProjectSettings, saveProjectSettings } from 'store/entities/project-settings/project-settings';
import { projectNameUpdated } from 'store/entities/projects/projects';
import { CurrencyExchangeDeleteModal } from './currency-exchange-delete.modal';

const CurrencyTabContent = ({
  settings,
  getProjectSettings,
  saveProjectSettings,
  additionalCurrencyOptions,
  isAdding,
  currencyTemplate,
  currencyOptions,
}) => {
  const { t } = useTranslation();
  const { project } = useParams();
  const [additionalCurrenciesList, setAdditionalCurrenciesList] = useState([]);
  const [primaryCurrency, setPrimaryCurrency] = useState('');
  const [selectedCurrencyTemplateId, setSelectedCurrencyTemplateId] = useState(
    currencyTemplate.find((item) => item.isDefault)?.currencyTemplateId || null,
  );
  const [isRemoveCurrencyModalVisible, setIsRemoveCurrencyModalVisible] = useState(false);
  const [isApplyCurrencyTemplateModalVisible, setIsAplyCurrencyTemplateModalVisible] = useState(false);
  const [selectedIndexToRemove, setSelectedIndexToRemove] = useState(null);

  const currencyTemplateOptions = currencyTemplate
    ? [...currencyTemplate]
        .sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0))
        .map((item) => ({
          label: item.name,
          value: item.currencyTemplateId,
        }))
    : [];

  const mappedCurrencyOptions = React.useMemo(() => {
    return currencyOptions.map((c) => ({
      ...c,
      disabled: additionalCurrenciesList.some((cur) => cur.currency === c.value || primaryCurrency === c.value),
    }));
  }, [additionalCurrenciesList, currencyOptions, primaryCurrency]);

  useEffect(() => {
    getProjectSettings(project);
  }, []);

  useEffect(() => {
    if (settings) {
      setPrimaryCurrency(settings.primaryCurrency);
      setAdditionalCurrenciesList(settings.additionalCurrencies || []);
    }
  }, [settings]);

  const onSubmit = () => {
    saveProjectSettings(project, {
      ...settings,
      additionalCurrencies: additionalCurrenciesList,
      primaryCurrency,
    });
  };

  const onAddCurrency = () =>
    setAdditionalCurrenciesList([
      ...additionalCurrenciesList,
      {
        currency: mappedCurrencyOptions.filter((o) => !o.disabled)[0]?.value,
        exchangeRate: 100,
      },
    ]);

  const onChangeAdditionalCurrency = (ev, i) => {
    const newElement = [...additionalCurrenciesList];
    if (ev.target.name === 'currency') {
      newElement[i] = {
        ...newElement[i],
        currency: ev.target.value,
      };
    } else if (ev.target.name === 'exchangeRate') {
      newElement[i] = {
        ...newElement[i],
        exchangeRate: ev.target.value,
      };
    }
    setAdditionalCurrenciesList(newElement);
  };

  const handleConfirmRemove = () => {
    if (selectedIndexToRemove !== null) {
      const newCurrencies = [...additionalCurrenciesList];
      newCurrencies.splice(selectedIndexToRemove, 1);
      setAdditionalCurrenciesList(newCurrencies);

      setIsRemoveCurrencyModalVisible(false);
      setSelectedIndexToRemove(null);
    }
  };

  const handleConfirmApplyTemplate = () => {
    const selectedTemplate = currencyTemplate.find((item) => item.currencyTemplateId === selectedCurrencyTemplateId);
    if (selectedTemplate) {
      setAdditionalCurrenciesList(selectedTemplate.additionalCurrencies);
    }
    setIsAplyCurrencyTemplateModalVisible(false);
  };

  return (
    <>
      <div style={{ padding: 'var(--padding)' }}>
        {isAdding && (
          <Loader text={t(translations.adding)} theme="light" cover>
            <Spinner dark />
          </Loader>
        )}
        <Heading top>{t(translations.currency)}</Heading>
        <Field label={t(translations.currency_template)}>
          <Flex gap alignItems="center">
            <Select
              name={'currencyTemplate'}
              options={currencyTemplateOptions}
              value={selectedCurrencyTemplateId}
              onChange={(e) => setSelectedCurrencyTemplateId(e.target.value)}
              width="200px"
              small
            />
            <Button
              label={t(translations.apply)}
              onClick={() => {
                setIsAplyCurrencyTemplateModalVisible(true);
              }}
              colored
              small
            />
          </Flex>
        </Field>

        <Field label={t(translations.projectSettings_primaryCurrency)}>
          <Select
            name="primaryCurrency"
            options={mappedCurrencyOptions}
            value={primaryCurrency}
            onChange={(e) => {
              setPrimaryCurrency(e.target.value);
            }}
            disabled
          />
        </Field>
        <Table
          table={{
            fixedWidth: '100%',
            columnAlignment: ['left', 'right', 'left'],
            headers: [
              {
                cells: [
                  {
                    value: t(translations.projectSettings_additionalCurrency),
                  },
                  {
                    value: t(translations.projectSettings_exchangeRate),
                    style: { width: '120px' },
                  },
                  { value: '', style: { width: '90px' } },
                ],
                actions: [
                  {
                    primary: true,
                    label: t(translations.add),
                    icon: 'add',
                    disabled: additionalCurrenciesList.length >= 10,
                    onClick: () => {
                      onAddCurrency();
                    },
                  },
                ],
              },
            ],
            rows: additionalCurrenciesList.map((element, i) => ({
              cells: [
                {
                  name: 'currency',
                  type: 'Select',
                  options: additionalCurrencyOptions,
                  value: element.currency,
                  onChange: (ev) => {
                    onChangeAdditionalCurrency(ev, i);
                  },
                  autoLayerWidth: true,
                },
                {
                  name: 'exchangeRate',
                  type: 'Input',
                  value: element.exchangeRate,
                  onChange: (ev) => {
                    onChangeAdditionalCurrency(ev, i);
                  },
                },
                {
                  value: `= 1 USD`,
                  disabled: true,
                  style: {
                    opacity: '0.5',
                    cursor: 'default',
                  },
                },
              ],
              actions: [
                {
                  label: 'Delete',
                  icon: 'minus',
                  onClick: () => {
                    setSelectedIndexToRemove(i);
                    setIsRemoveCurrencyModalVisible(true);
                  },
                },
              ],
            })),
          }}
        />
        <Spacer />
        <Button label={t(translations.save)} onClick={onSubmit} disabled={isAdding} loading={isAdding} colored />
      </div>
      {isRemoveCurrencyModalVisible && (
        <CurrencyExchangeDeleteModal
          onSubmit={handleConfirmRemove}
          onClose={() => {
            setIsRemoveCurrencyModalVisible(false);
            setSelectedIndexToRemove(null);
          }}
          title={t(translations.delete_currency_exchange_rate_title)}
          message={t(translations.delete_currency_exchange_confirmation)}
          submitButtonName={t(translations.delete)}
        />
      )}
      {isApplyCurrencyTemplateModalVisible && (
        <CurrencyExchangeDeleteModal
          onSubmit={handleConfirmApplyTemplate}
          onClose={() => {
            setIsAplyCurrencyTemplateModalVisible(false);
          }}
          title={t(translations.apply_currency_template_title)}
          message={t(translations.apply_currency_template_confirmation)}
          submitButtonName={t(translations.apply)}
        />
      )}
    </>
  );
};

const mapStateToProps = ({ entities }) => {
  const {
    projectSettings: { currencies, settings, isAdding, isFetching },
  } = entities;
  const { currencyTemplate } = entities.companySettings;

  const currencyOptions = currencies.map((item) => ({
    label: `(${item.code}) ${item.name}`,
    value: item.code,
  }));

  const additionalCurrencyOptions = currencies
    .filter((item) => item.code !== 'USD')
    .map((item) => ({
      label: `(${item.code}) ${item.name}`,
      value: item.code,
    }));

  return {
    isFetching,
    isAdding,
    currencies,
    settings,
    additionalCurrencyOptions,
    currencyTemplate,
    currencyOptions,
  };
};

const mapDispatchToProps = {
  getProjectSettings,
  saveProjectSettings,
  projectNameUpdated,
};

const Container = connect(mapStateToProps, mapDispatchToProps)(CurrencyTabContent);

export { Container as CurrencyTabContent };
